import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useLibraryStatus } from '@/hooks/dashboard/useLibraryStatus'
import { bookListUrl } from '@/lib/utils'
import type { BookStatus, BookType } from '@/types'
import { ChartContainer } from '../ChartContainer'

// Types for Recharts custom components
interface TooltipPayload {
  value: number
  dataKey: string
  color?: string
  payload: {
    typeName: string
    read: number
    reading: number
    unread: number
    total: number
  }
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayload[]
  label?: string
}

export function LibraryStatusChart() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isLoading, error } = useLibraryStatus()

  const title = t('dashboard.charts.libraryStatus')
  const description = t('dashboard.charts.libraryStatusDescription')

  // Transform data for stacked bar chart
  const chartData = data.map((item) => ({
    name: item.typeName,
    type: item.type,
    [t('books.status.read')]: item.read,
    [t('books.status.reading')]: item.reading,
    [t('books.status.unread')]: item.unread,
  }))

  // Clicking a bar segment opens the list for that type + status
  const barClickHandler = (status: BookStatus) => (entry: { payload?: { type?: BookType } }) => {
    if (entry.payload?.type) navigate(bookListUrl({ type: entry.payload.type, status }))
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            {payload.map((entry) => (
              <div key={entry.dataKey} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color || '#000' }}
                />
                <span className="text-muted-foreground">{entry.dataKey}:</span>
                <span className="font-medium text-foreground">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }
    return null
  }

  // Show empty state if no data
  if (!isLoading && !error && data.length === 0) {
    return (
      <ChartContainer title={title} description={t('dashboard.charts.noData')}>
        <div className="flex items-center justify-center h-full text-center text-muted-foreground">
          <div>
            <div className="text-4xl mb-4">📚</div>
            <p className="font-medium mb-1">{t('dashboard.charts.noDataTitle')}</p>
            <p className="text-sm">{t('dashboard.charts.addBooksToSeeStatus')}</p>
          </div>
        </div>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer
      title={title}
      description={description}
      isLoading={isLoading}
      error={error?.message || null}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            className="text-xs text-muted-foreground"
          />
          <YAxis axisLine={false} tickLine={false} className="text-xs text-muted-foreground" />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '12px',
            }}
          />
          <Bar
            dataKey={t('books.status.read')}
            stackId="a"
            fill="#10B981"
            radius={[0, 0, 0, 0]}
            cursor="pointer"
            onClick={barClickHandler('read')}
          />
          <Bar
            dataKey={t('books.status.reading')}
            stackId="a"
            fill="#06B6D4"
            radius={[0, 0, 0, 0]}
            cursor="pointer"
            onClick={barClickHandler('reading')}
          />
          <Bar
            dataKey={t('books.status.unread')}
            stackId="a"
            fill="#EC4899"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={barClickHandler('unread')}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
